import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { authKey, orgKey } from "@/hooks/useAuth";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

export function FacebookLoginSuccess() {
  const [isSuccess, setIsSuccess] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const hasExchangedCodeRef = useRef(false);
  const router = useRouter();

  const exchangeCodeMutation = useMutation({
    mutationFn: async (code: string) => {
      const apiUrl = import.meta.env.VITE_API_URL || "https://api.zora.cloud";
      const response = await fetch(
        `${apiUrl}/webhooks/facebook/exchange-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem(authKey)}`,
            "x-org-id": localStorage.getItem(orgKey) || "",
          },
          body: JSON.stringify({
            code,
            redirectUri: "https://app.zora.cloud/settings/facebook-success",
          }),
        },
      );
      if (!response.ok) {
        throw new Error("Failed to exchange token");
      }
      return response.json();
    },
    // biome-ignore lint: @typescript-eslint/no-unused-vars
    onSuccess: (data) => {
      setIsSuccess(true);
      setShowModal(true);
    },
    onError: (error) => {
      console.error("Error:", error);
      setIsSuccess(false);
    },
  });

  const exchangeCode = useCallback(
    (code: string) => {
      if (hasExchangedCodeRef.current) return;
      exchangeCodeMutation.mutate(code);
      hasExchangedCodeRef.current = true;
    },
    [exchangeCodeMutation],
  );

  const handleCloseModal = () => {
    setShowModal(false);
    // Redirect to connections page to see the updated status
    router.navigate({ to: "/settings/connections" });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("code");
    const error = searchParams.get("error");

    if (error) {
      console.error("Login error:", error);
    } else if (code) {
      exchangeCode(code);
    }
  }, [exchangeCode]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900/50">
      <Card className="w-[350px]">
        <CardHeader>
          <CardTitle className="text-center">Facebook Login</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center">
          {!isSuccess && (
            <>
              <Spinner />
              <p className="text-center text-muted-foreground">
                Processing login...
              </p>
            </>
          )}
          {isSuccess && (
            <p className="text-center text-muted-foreground">All done!</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Login Successful</DialogTitle>
          </DialogHeader>
          <p>Your Facebook login was successful!</p>
          <DialogFooter>
            <Button onClick={handleCloseModal}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute(
  "/_authenticated/settings/facebook-success",
)({
  component: () => <FacebookLoginSuccess />,
});
